import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { IQuote } from '../types/Quotes';
import { getQuotes, deleteQuote as apiDeleteQuote, toggleQuoteReadStatus, toggleQuoteArchived } from '../services/QuoteService';
import QuoteModal from '../components/quotes/QuoteModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';
import styles from '../styles/Dashboard.module.css'; 
import quoteStyles from '../styles/Quotes.module.css'; 
import { TrashIcon, EyeIcon, EyeSlashIcon, ArchiveBoxIcon, ArchiveBoxXMarkIcon } from '@heroicons/react/24/outline';
import { DocumentTextIcon, HomeModernIcon, BuildingStorefrontIcon } from '@heroicons/react/24/solid';
import { useToast } from '../hooks/useToast';

const QuotesPage: React.FC = () => {
    const [quotes, setQuotes] = useState<IQuote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedQuote, setSelectedQuote] = useState<IQuote | null>(null);
    const toast = useToast();
    const { isOpen, config, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchQuotes = async () => {
        setLoading(true);
        setError(null);
        try {
            const fetchedQuotes = await getQuotes();
            setQuotes(fetchedQuotes);
        } catch (err: any) {
            setError(err.message || "Fallo al cargar las cotizaciones desde el servidor.");
            toast.error('Error', 'No se pudieron cargar las cotizaciones');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotes();
    }, []);

    const handleDelete = async (id: number, quoteName: string) => {
        const confirmed = await confirm({
            title: 'Eliminar Cotización',
            message: `¿Estás seguro de eliminar la cotización de: "${quoteName}"? Esta acción no se puede deshacer.`,
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            type: 'danger'
        });

        if (confirmed) {
            try {
                // ✅ CORRECCIÓN: No verificar el "success" ya que la API probablemente 
                // solo lanza error si falla, o retorna algo diferente
                await apiDeleteQuote(id);
                
                // ✅ Actualización optimista: eliminar inmediatamente de la UI
                setQuotes(prev => prev.filter(quote => quote.id !== id));
                
                toast.success('Éxito', `Cotización de "${quoteName}" eliminada con éxito.`);
                
            } catch (err: any) {
                // ✅ Si hay error, revertir la actualización optimista
                fetchQuotes(); // Recargar para sincronizar con el servidor
                toast.error('Error', `Error al eliminar: ${err.message}`);
            }
        }
    };

    const handleToggleRead = async (id: number, currentState: boolean) => {
        try {
            const newReadStatus = !currentState;
            await toggleQuoteReadStatus(id, newReadStatus); 
            
            // Actualización optimista
            setQuotes(prevQuotes =>
                prevQuotes.map(item =>
                    item.id === id ? { ...item, isRead: newReadStatus } : item
                )
            );
            
            toast.info('Estado actualizado', `Cotización marcada como ${newReadStatus ? 'leída' : 'no leída'}`);
        } catch (err: any) {
            toast.error('Error', `Error al actualizar estado de lectura: ${err.message}`);
            // Revertir en caso de error
            fetchQuotes();
        }
    };

    const handleToggleArchive = async (id: number, currentState: boolean, quoteName: string) => {
        const actionType = currentState ? 'unarchive' : 'archive';
        const confirmed = await confirm({
            title: currentState ? 'Desarchivar Cotización' : 'Archivar Cotización',
            message: currentState 
                ? `¿Estás seguro de desarchivar la cotización de: "${quoteName}"?`
                : `¿Estás seguro de archivar la cotización de: "${quoteName}"?`,
            confirmText: currentState ? 'Desarchivar' : 'Archivar',
            cancelText: 'Cancelar',
            type: 'warning'
        });

        if (confirmed) {
            try {
                const newArchiveStatus = !currentState;
                await toggleQuoteArchived(id, newArchiveStatus); 
                
                // Actualización optimista
                setQuotes(prevQuotes =>
                    prevQuotes.map(item =>
                        item.id === id ? { ...item, isArchived: newArchiveStatus } : item
                    )
                );
                
                toast.info('Estado actualizado', `Cotización ${newArchiveStatus ? 'archivada' : 'desarchivada'}`);
            } catch (err: any) {
                toast.error('Error', `Error al actualizar estado de archivado: ${err.message}`);
                // Revertir en caso de error
                fetchQuotes();
            }
        }
    };
    
    const handleViewQuote = async (quote: IQuote) => {
        setSelectedQuote(quote);

        if (quote.isRead) {
            return;
        }

        // Actualización optimista
        setQuotes(prevQuotes =>
            prevQuotes.map(item =>
                item.id === quote.id ? { ...item, isRead: true } : item
            )
        );

        try {
            await toggleQuoteReadStatus(quote.id, true);
        } catch (err: any) {
            toast.error('Error', 'No se pudo marcar como leído. Reintentando...');
            console.error("Fallo al marcar como leído:", err);
            
            // Revertir en caso de error
            setQuotes(prevQuotes =>
                prevQuotes.map(item =>
                    item.id === quote.id ? { ...item, isRead: false } : item
                )
            );
        }
    };

    const handleCloseModal = () => {
        setSelectedQuote(null);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', { 
            day: 'numeric', 
            month: 'short',
            year: 'numeric'
        });
    };

    const getProjectTypeIcon = (type: string) => {
        return type === 'new' ? 
            <HomeModernIcon className={quoteStyles.newProjectIcon} /> : 
            <BuildingStorefrontIcon className={quoteStyles.remodelIcon} />;
    };

    const getProjectTypeText = (type: string) => {
        return type === 'new' ? 'Nuevo Proyecto' : 'Remodelación';
    };

    const getCategoryText = (category: string) => {
        const categories: { [key: string]: string } = {
            'residencial': 'Residencial',
            'comercial': 'Comercial',
            'institucional': 'Institucional',
            'paisajismo': 'Paisajismo',
            'otros': 'Otros'
        };
        return categories[category] || category;
    };

    if (loading) {
        return <AdminLayout pageTitle="Gestión de Cotizaciones"><p>Cargando cotizaciones...</p></AdminLayout>;
    }
    
    if (error) { 
        return <AdminLayout pageTitle="Gestión de Cotizaciones"><p style={{ color: 'red', textAlign: 'center' }}>Error de Conexión: {error}</p></AdminLayout>;
    }

    return (
        <AdminLayout pageTitle="Gestión de Cotizaciones">
            <div className={quoteStyles.statsBar}>
                <div className={quoteStyles.statItem}>
                    <span className={quoteStyles.statValue}>{quotes.filter(q => !q.isRead).length}</span>
                    <span className={quoteStyles.statLabel}>Sin Leer</span>
                </div>
                <div className={quoteStyles.statItem}>
                    <span className={quoteStyles.statValue}>{quotes.filter(q => q.projectType === 'new').length}</span>
                    <span className={quoteStyles.statLabel}>Nuevos Proyectos</span>
                </div>
                <div className={quoteStyles.statItem}>
                    <span className={quoteStyles.statValue}>{quotes.filter(q => q.projectType === 'remodel').length}</span>
                    <span className={quoteStyles.statLabel}>Remodelaciones</span>
                </div>
                <div className={quoteStyles.statItem}>
                    <span className={quoteStyles.statValue}>{quotes.filter(q => q.isArchived).length}</span>
                    <span className={quoteStyles.statLabel}>Archivadas</span>
                </div>
            </div>

            <div className={styles.recentActivity}>
                <table className={styles.projectTable}>
                    <thead>
                        <tr className={quoteStyles.headerRow}>
                            <th>Estado</th>
                            <th>Tipo</th>
                            <th>Cliente</th>
                            <th>Proyecto</th>
                            <th>Ubicación</th>
                            <th>Fecha</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {quotes.map((quote) => (
                            <tr 
                                key={quote.id} 
                                className={quote.isRead ? quoteStyles.readRow : quoteStyles.unreadRow}
                                onClick={() => handleViewQuote(quote)}
                            >
                                <td>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleToggleRead(quote.id, quote.isRead); }}
                                        title={quote.isRead ? "Marcar como No Leído" : "Marcar como Leído"}
                                        className={`${styles.iconButton} ${quote.isRead ? quoteStyles.readIcon : quoteStyles.unreadIcon}`}
                                    >
                                        {quote.isRead ? <EyeIcon className={styles.iconButtonIcon} /> : <EyeSlashIcon className={styles.iconButtonIcon} />}
                                    </button>
                                </td>
                                <td>
                                    <div className={quoteStyles.typeBadge} title={getProjectTypeText(quote.projectType)}>
                                        {getProjectTypeIcon(quote.projectType)}
                                        <span className={quoteStyles.typeText}>
                                            {getProjectTypeText(quote.projectType)}
                                        </span>
                                    </div>
                                </td>
                                <td className={quoteStyles.clientCell}>
                                    <div className={quoteStyles.clientName}>{quote.quoteName}</div>
                                    <div className={quoteStyles.clientContact}>
                                        {quote.quoteEmail} • {quote.quotePhone}
                                    </div>
                                </td>
                                <td className={quoteStyles.projectCell}>
                                    <div className={quoteStyles.projectCategory}>
                                        {getCategoryText(quote.projectCategory)}
                                    </div>
                                    <div className={quoteStyles.projectDescription} title={quote.projectDescription}>
                                        {quote.projectDescription.substring(0, 60)}...
                                    </div>
                                    <div className={quoteStyles.projectSize}>
                                        Tamaño: {quote.projectSize}
                                    </div>
                                </td>
                                <td className={quoteStyles.locationCell}>
                                    {quote.projectLocation}
                                </td>
                                <td>{formatDate(quote.createdAt)}</td>
                                <td>
                                    <div className={styles.actionsCell}>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleViewQuote(quote); }} 
                                            title="Ver Detalles" 
                                            className={quoteStyles.viewButton}
                                        >
                                            <DocumentTextIcon className={styles.iconButtonIcon} />
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleToggleArchive(quote.id, quote.isArchived, quote.quoteName); }} 
                                            title={quote.isArchived ? "Desarchivar" : "Archivar"}
                                            className={quote.isArchived ? quoteStyles.unarchiveButton : quoteStyles.archiveButton}
                                        >
                                            {quote.isArchived ? 
                                                <ArchiveBoxXMarkIcon className={styles.iconButtonIcon} /> : 
                                                <ArchiveBoxIcon className={styles.iconButtonIcon} />
                                            }
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDelete(quote.id, quote.quoteName); }} 
                                            title="Eliminar" 
                                            className={styles.deleteButton}
                                        >
                                            <TrashIcon className={styles.iconButtonIcon} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {quotes.length === 0 && <p className={styles.noProjects}>No hay cotizaciones registradas.</p>}
            </div>
            
            {/* Modal de Confirmación */}
            <ConfirmModal
                isOpen={isOpen}
                title={config?.title || ''}
                message={config?.message || ''}
                confirmText={config?.confirmText}
                cancelText={config?.cancelText}
                type={config?.type}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />

            {/* Modal de Cotización */}
            {selectedQuote && (
                <QuoteModal quote={selectedQuote} onClose={handleCloseModal} />
            )}
        </AdminLayout>
    );
};

export default QuotesPage;